import { useState } from 'react'

import EyeOffIcon from '@heroicons/react/outline/EyeOffIcon'
import PlusIcon from '@heroicons/react/outline/PlusIcon'
import XIcon from '@heroicons/react/outline/XIcon'
import { Link } from 'react-router-dom'

import { CustomFieldSchema } from '@tribeplatform/gql-client/types'
import {
  useAddMemberSchemaField,
  useArchiveMemberSchemaField,
  useAuthToken,
  useNetwork,
  useUnArchiveMemberSchemaField,
} from '@tribeplatform/react-sdk/hooks'
import { Accordion } from '@tribeplatform/react-ui-kit/Accordion'
import { BackgroundProvider } from '@tribeplatform/react-ui-kit/BackgroundContext'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { confirm } from '@tribeplatform/react-ui-kit/Dialog'
import { List } from '@tribeplatform/react-ui-kit/Layout'

import { FieldModal } from '../../CustomField/FieldModal'
import { getFieldIcon, getFieldSetting } from '../../CustomField/utils'
import { MemberAbout } from '../../Member/MemberAbout'
import { MemberHeader } from '../../Member/MemberHeader'
import { PostCardListLoading } from '../../PostList/GenericPostList'
import { suggestedFields } from './suggestedFields'

export const FieldRow = ({ field, setHighlightedField, setFieldModal }) => {
  const { mutateAsync: archiveField } = useArchiveMemberSchemaField()
  const { mutateAsync: unArchiveField } = useUnArchiveMemberSchemaField()

  return (
    <List.Item
      key={field.key}
      className="group"
      onMouseEnter={() => setHighlightedField(field.key)}
      onMouseLeave={() => setHighlightedField('')}
    >
      <Link
        onClick={() => setFieldModal(field)}
        to="#"
        className=" flex-1 border border-surface-200/50 rounded-md p-2 px-3 mb-2 bg-surface-50 hover:bg-surface-100 flex space-x-2 items-center"
      >
        <span className="w-5 h-5 flex-shrink-0">{getFieldIcon(field)}</span>
        <span className="flex-grow">{field.name}</span>
        {field.archived ? (
          <>
            <Button
              onClick={async e => {
                e.preventDefault()
                e.stopPropagation()
                const confirmed = await confirm({
                  title: `Do you want to unarchive ${field.name} (${field.key})?`,
                  proceedLabel: 'Unarchive',
                })

                if (!confirmed) return

                unArchiveField({ key: field.key })
              }}
              className="opacity-0 group-hover:opacity-100"
              variant="basic"
              leadingIcon={<PlusIcon />}
              size="xs"
              rounded
            />
            <div className="pr-1 block group-hover:hidden">
              <EyeOffIcon className="w-4 h-4 text-basicSurface-300" />
            </div>
          </>
        ) : (
          <Button
            onClick={async e => {
              e.preventDefault()
              e.stopPropagation()
              const confirmed = await confirm({
                title: `Are you sure you want to archive ${field.name} (${field.key})?`,
                description:
                  'Archiving a field will not delete the data for the field, therefore, this action is reversible and you can always unarchive!',
                proceedLabel: 'Archive',
                danger: true,
              })

              if (!confirmed) return

              archiveField({ key: field.key })
            }}
            className="opacity-0 group-hover:opacity-100"
            variant="basic"
            leadingIcon={<XIcon />}
            size="xs"
            rounded
          />
        )}
      </Link>
    </List.Item>
  )
}

export const MemberFields = () => {
  const {
    data: { member },
  } = useAuthToken()
  const {
    data: {
      memberFields: { fields },
    },
  } = useNetwork()

  const [fieldModal, setFieldModal] = useState<Partial<CustomFieldSchema>>(null)

  const [highlightedField, setHighlightedField] = useState('')
  const { mutateAsync: addField } = useAddMemberSchemaField()

  const availableFields = suggestedFields.filter(
    aField => fields.findIndex(f => f.key === aField.key) === -1,
  )

  return (
    <div className="grid grid-cols-12 h-screen">
      <div className="col-span-12 lg:col-span-4 bg-surface-50 p-5 flex flex-col space-y-5 text-basicSurface-500 max-h-screen overflow-auto">
        <BackgroundProvider backgroundType="surface">
          <div className="flex items-center space-x-2">
            <h4 className="flex-grow">Member fields</h4>
            <Button
              variant="outline"
              leadingIcon={<PlusIcon />}
              size="sm"
              className="flex-shrink-0"
              onClick={() => {
                setFieldModal({})
              }}
            >
              Add field
            </Button>
          </div>
          <div>
            <List spacing="none">
              {fields
                .filter(f => !f.archived)
                .sort(a => {
                  const section = getFieldSetting(a, 'section')
                  return section ? 1 : -1
                })
                .map(field => {
                  return (
                    <FieldRow
                      key={field.key}
                      field={field}
                      setHighlightedField={setHighlightedField}
                      setFieldModal={setFieldModal}
                    />
                  )
                })}
              {!fields.filter(f => !f.archived)?.length ? (
                <p>You have not added any profile fields.</p>
              ) : null}
            </List>
            {fields.filter(f => f.archived)?.length ? (
              <Accordion>
                <Accordion.Button>Archived fields</Accordion.Button>
                <Accordion.Panel className="flex-1">
                  <List spacing="none">
                    {fields
                      .filter(f => f.archived)
                      .map(field => {
                        return (
                          <FieldRow
                            key={field.key}
                            field={field}
                            setHighlightedField={setHighlightedField}
                            setFieldModal={setFieldModal}
                          />
                        )
                      })}
                  </List>
                </Accordion.Panel>
              </Accordion>
            ) : null}
          </div>
          <h4>Suggested fields</h4>
          <List spacing="none">
            {availableFields?.length ? (
              availableFields.map(field => {
                return (
                  <List.Item key={field.key}>
                    <Link
                      to="#"
                      onClick={async e => {
                        e.preventDefault()
                        const confirmed = await confirm({
                          title: `Are you sure you want to add ${field.name} (${field.key}) field?`,
                          description:
                            'Suggested fields come with predefined validation, UI, and values. You will be able to archive them in the future.',
                          proceedLabel: 'Add field',
                        })

                        if (!confirmed) return

                        addField({ input: field })
                      }}
                      className="group border border-surface-200/50 rounded-md p-2 px-3 mb-2 bg-surface-50 hover:bg-surface-100 flex space-x-2 items-center"
                    >
                      <span className="w-5 h-5 flex-shrink-0">
                        {getFieldIcon(field)}
                      </span>
                      <span className="flex-grow">{field.name}</span>
                      <PlusIcon className="opacity-0 group-hover:opacity-100 w-4 h-4" />
                    </Link>
                  </List.Item>
                )
              })
            ) : (
              <div>You&apos;ve already added all the available fields.</div>
            )}
          </List>
        </BackgroundProvider>
      </div>
      <div className="hidden lg:flex col-span-8 p-5 flex-col space-y-5 max-h-screen overflow-auto">
        <div className="flex-shrink-0">
          <MemberHeader member={member} attached="none" />
        </div>
        <div className="grid grid-cols-12 gap-5">
          <div className="col-span-8 flex flex-col space-y-5">
            <PostCardListLoading pulse={false} count={3} />
          </div>
          <div className="col-span-4">
            <MemberAbout
              preview
              highlightedField={highlightedField}
              member={member}
              memberFields={fields}
            />
          </div>
        </div>
      </div>
      {!!fieldModal && (
        <FieldModal
          field={fieldModal}
          open
          onClose={() => setFieldModal(null)}
        />
      )}
    </div>
  )
}
